// The password rules the customer Cognito pool enforces, checked before a
// password is sent. Where they come from: goldie-cloud's Terraform gives the
// customer pool no password_policy (infrastructure/terraform/cognito/main.tf),
// so Cognito's default applies: 8 or more characters with an upper-case letter,
// a lower-case letter, a number and a symbol. scripts/cognito_user_import.py
// records the same rules from production, and staging refused a password
// without a capital or a symbol on 19 Oct 2026. Cognito also refuses a password
// that starts or ends with a space, or is longer than 256 characters.
//
// The strength meter (zxcvbn) does not know these rules: it rated
// "quietlanternharbor2031" Excellent, and Cognito refused it. The API checks
// the same rules (goldie-cloud routers/api_router_cognito.py), so the two must
// change together.

// Cognito's special characters, as its documentation lists them, plus a space
// that is neither first nor last.
const SPECIAL_CHARACTERS = new Set('^$*.[]{}()?"!@#%&/\\,><\':;|_~`=+- ')

export interface PasswordRule {
  key: string
  label: string
  met: boolean
}

export const PASSWORD_MIN_LENGTH = 8
export const PASSWORD_MAX_LENGTH = 256

// The five rules the checklist always shows, in the order it shows them.
export function passwordRules(password: string): PasswordRule[] {
  const characters = Array.from(password)
  return [
    {
      key: 'length',
      label: `${PASSWORD_MIN_LENGTH} or more characters`,
      met: characters.length >= PASSWORD_MIN_LENGTH
    },
    {
      key: 'upper',
      label: 'A capital letter',
      met: /[A-Z]/.test(password)
    },
    {
      key: 'lower',
      label: 'A lowercase letter',
      met: /[a-z]/.test(password)
    },
    { key: 'number', label: 'A number', met: /[0-9]/.test(password) },
    {
      key: 'special',
      label: 'A special character, like ! or ?',
      met: characters.some(
        (c, i) =>
          SPECIAL_CHARACTERS.has(c) &&
          (c !== ' ' || (i > 0 && i < characters.length - 1))
      )
    }
  ]
}

// Rules the checklist shows only when they are broken.
export function passwordProblems(password: string): string[] {
  const problems: string[] = []
  if (password !== password.trim()) {
    problems.push('No space at the start or end')
  }
  if (Array.from(password).length > PASSWORD_MAX_LENGTH) {
    problems.push(`No more than ${PASSWORD_MAX_LENGTH} characters`)
  }
  return problems
}

export function meetsPasswordPolicy(password: string): boolean {
  return (
    passwordRules(password).every(rule => rule.met) &&
    passwordProblems(password).length === 0
  )
}
